import TextField, { TextFieldProps } from "@mui/material/TextField";
import classnames from "classnames";
import omit from "lodash-es/omit";
import React, { useState } from "react";

import { ErrorIcon } from "components/Icons";

import styles from "./styles.module.scss";

/**
 * @deprecated
 * This component is deprecated and will be removed once we update all pages and components to the new design
 * Please use `<TextInput />` instead.
 */

type InputFieldProps = {
  type: string;
  name: string;
  label?: React.ReactNode;
  value?: string;
  error?: boolean;

  handleChange?: (name: string, value: string) => void;
  handleBlur?: (name: string, value: string) => void;
  handleFocus?: (name: string, value: string) => void;
  handleKeyDown?: (event: React.KeyboardEvent<Element>) => void;
  handleKeyPress?: (event: React.KeyboardEvent<Element>) => void;
  onAutofill?: (name: string) => void;
  fullWidth?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  focused?: boolean;
  testId?: string;
  inputRef?: (ref: HTMLInputElement) => void;
  helperText?: React.ReactNode;
  InputLabelProps?: any;
  errorText?: string;
  classes?: any;
  errorTextOverflow?: boolean;
  placeholder?: string;
  defaultValue?: string;
  showErrorContainer?: boolean;
  readOnly?: boolean;
} & Omit<TextFieldProps, "variant">;

type ErrorMessageProps = {
  errorText?: InputFieldProps["errorText"];
  testId?: InputFieldProps["testId"];
  errorTextOverflow?: InputFieldProps["errorTextOverflow"];
  addWrapper?: boolean;
};

export const ErrorMessage = (props: ErrorMessageProps) => {
  const error = (
    <div
      className={classnames(styles.errorTextContainer, {
        [styles.active]: !!props.errorText,
      })}
      title={props.errorText ? props.errorText : undefined}
      data-testid={props.testId ? `${props.testId}-error` : `input-field-error`}
    >
      <ErrorIcon className={styles.errorIcon} />
      <span
        className={classnames(styles.errorText, {
          [styles.overflowVisible]: !!props.errorTextOverflow,
        })}
      >
        {props.errorText}
      </span>
    </div>
  );

  return props.addWrapper ? (
    <div className={styles.errorHelperContainer}>{error}</div>
  ) : (
    error
  );
};

const InputField: React.FC<InputFieldProps> = props => {
  const { autoFocus, showErrorContainer = true } = props;
  const [focused, setFocused] = useState(autoFocus);

  const testAttributes = props?.testId
    ? { "data-testid": `${props?.testId}` }
    : {};

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = ({
    target,
  }) => {
    if (props.handleChange) {
      props.handleChange(target.name, target.value);
    }
  };

  const handleBlur: React.FocusEventHandler<HTMLInputElement> = ({
    target,
  }) => {
    setFocused(false);
    if (props.handleBlur) {
      props.handleBlur(target.name, target.value);
    }
  };

  const handleFocus: React.FocusEventHandler<HTMLInputElement> = ({
    target,
  }) => {
    setFocused(true);
    if (props.handleFocus) {
      props.handleFocus(target.name, target.value);
    }
  };

  const handleKeyDown: React.KeyboardEventHandler<Element> = event => {
    if (props.handleKeyDown) {
      props.handleKeyDown(event);
    }
  };

  const textFieldProps = omit(props, [
    "testId",
    "errorText",
    "errorTextOverflow",
    "showErrorContainer",
    "onAutofill",
    "onAutofillDrop",
  ]);

  return (
    <TextField
      classes={{ root: classnames(styles.root, props?.classes?.root) }}
      {...textFieldProps}
      helperText={showErrorContainer ? <ErrorMessage {...props} /> : undefined}
      slotProps={{
        input: {
          ...props?.slotProps?.input,
          readOnly: props.readOnly,
          classes: {
            root: classnames(
              styles.inputRoot,
              // @ts-ignore
              props?.slotProps?.input?.classes?.root
            ),
            error: styles.inputError,
            input: styles.input,
          },
        },
        htmlInput: {
          ...props.slotProps?.htmlInput,
          ...testAttributes,
        },
        formHelperText: {
          classes: { root: styles.errorHelperContainer },
          component: "div",
        },
        inputLabel: {
          classes: {
            asterisk: styles.asterisk,
            root: styles.label,
          },
          shrink: props.value || focused ? undefined : false,
          ...props?.slotProps?.inputLabel,
        },
      }}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      placeholder={props.placeholder}
      inputRef={props.inputRef}
      variant="standard"
    />
  );
};

export default InputField;
